import React from "react";

import { Smartphone } from "lucide-react";
import { useTranslation } from "react-i18next";

const GooglePlayIcon = () => (
  <svg
    viewBox="0 0 48 48"
    className="h-6 w-6 shrink-0"
    aria-hidden="true"
    focusable="false"
  >
    <path
      fill="#4db6ac"
      d="M7.6,5.4C7.1,5.9,6.8,6.7,6.8,7.6v32.8c0,0.9,0.3,1.7,0.8,2.2l17.3-17.3L7.6,5.4z"
    />
    <path
      fill="#f44336"
      d="M33.9,26.1l-8.9-8.9L7.6,42.6c0.5,0.4,1.2,0.5,1.9,0.1L33.9,26.1z"
    />
    <path
      fill="#ffc107"
      d="M33.9,21.9l7.4-4.3c0.9-0.5,0.9-1.9,0-2.4l-7.4-4.3l-8.9,8.9L33.9,21.9z"
    />
    <path
      fill="#2196f3"
      d="M7.6,5.4l17.3,17.3l8.9-8.9L9.5,5.3C8.8,4.9,8.1,5,7.6,5.4z"
    />
  </svg>
);

const AppleIcon = () => (
  <svg
    viewBox="0 0 24 24"
    className="h-6 w-6 shrink-0"
    aria-hidden="true"
    focusable="false"
  >
    <path
      fill="currentColor"
      d="M18.7 12.7c0-2.1 1.7-3.1 1.8-3.2-1-1.4-2.5-1.6-3.1-1.6-1.3-.1-2.5.8-3.2.8-.7 0-1.7-.7-2.8-.7-1.4 0-2.8.9-3.5 2.2-1.5 2.6-.4 6.5 1.1 8.6.7 1.1 1.6 2.3 2.7 2.2 1.1-.1 1.5-.7 2.8-.7s1.6.7 2.8.7 1.9-1.1 2.6-2.1c.8-1.2 1.1-2.3 1.2-2.4-.1 0-2.2-.8-2.4-3.8zM15.5 5.9c.6-.7 1-1.7.9-2.7-.9.1-1.9.6-2.5 1.3-.5.6-1 1.6-.9 2.5 1 .1 1.9-.5 2.5-1.1z"
    />
  </svg>
);

const StoreBadge = ({ href, icon, prefix, label, ariaLabel }) => (
  <a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    aria-label={ariaLabel}
    className="inline-flex h-12 shrink-0 items-center gap-3 rounded-xl bg-cfPrimary px-[0.875rem] py-2 text-cfSecondary no-underline shadow-[0_8px_20px_-8px_var(--cf-primary-color)] transition-all hover:bg-cfPrimaryDark hover:text-cfSecondary hover:no-underline active:scale-[0.98] focus:outline-none focus-visible:ring-2 focus-visible:ring-cfPrimary focus-visible:ring-offset-2"
  >
    <span className="flex h-6 w-6 shrink-0 items-center justify-center">
      {icon}
    </span>
    <span className="flex flex-col items-start text-left leading-none">
      <span className="whitespace-nowrap text-[9px] font-medium tracking-[0.04em] opacity-85">
        {prefix}
      </span>
      <span className="mt-0.5 whitespace-nowrap text-[15px] font-semibold tracking-tight">
        {label}
      </span>
    </span>
  </a>
);

const WalletStoreFallback = ({ iosUrl, androidUrl }) => {
  const { t } = useTranslation(["walletOffer"]);
  const showIos = Boolean(iosUrl);
  const showAndroid = Boolean(androidUrl);

  if (!showIos && !showAndroid) return null;

  return (
    <section className="animate-fadeIn rounded-2xl border border-gray-200 bg-white p-[1.25rem] shadow-sm sm:p-5">
      <div className="flex flex-col items-center gap-4 text-center sm:flex-row sm:items-center sm:justify-between sm:gap-6 sm:text-left">
        <div className="flex min-w-0 items-start justify-center gap-3 sm:justify-start">
          <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-cfTableHeaderBackgroundColor text-cfPrimary">
            <Smartphone className="h-5 w-5" aria-hidden="true" />
          </span>
          <div className="min-w-0 text-left">
            <h2 className="mb-0 text-sm font-semibold text-cfHeadingText sm:text-base">
              {t("walletOffer:fallback_title")}
            </h2>
            <p className="mb-0 mt-0.5 max-w-md text-xs leading-relaxed text-gray-500 sm:text-sm">
              {t("walletOffer:fallback_description")}
            </p>
          </div>
        </div>

        <div className="flex flex-wrap items-center justify-center gap-2.5 sm:justify-end">
          {showAndroid ? (
            <StoreBadge
              href={androidUrl}
              icon={<GooglePlayIcon />}
              prefix={t("walletOffer:play_store_prefix")}
              label={t("walletOffer:play_store")}
              ariaLabel={`${t("walletOffer:play_store_prefix")} ${t("walletOffer:play_store")}`}
            />
          ) : null}
          {showIos ? (
            <StoreBadge
              href={iosUrl}
              icon={<AppleIcon />}
              prefix={t("walletOffer:app_store_prefix")}
              label={t("walletOffer:app_store")}
              ariaLabel={`${t("walletOffer:app_store_prefix")} ${t("walletOffer:app_store")}`}
            />
          ) : null}
        </div>
      </div>
    </section>
  );
};

export default WalletStoreFallback;
